import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { compact, isEmpty } from 'lodash'

function parseArrayParam(value) {
  if (!value) return []
  return compact(value.split(','))
}

function serializeArrayParam(arr) {
  if (!arr || arr.length === 0) return ''
  return arr.join(',')
}

function useLogFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse filters from URL search params (comma-separated arrays)
  const filters = useMemo(() => {
    return {
      projectIds: parseArrayParam(searchParams.get('projectIds')),
      serviceIds: parseArrayParam(searchParams.get('serviceIds')),
      severities: parseArrayParam(searchParams.get('severities')),
    }
  }, [searchParams])

  // Update URL when filters change
  const setFilters = useCallback((newFilters) => {
    const params = {}
    const projectIds = serializeArrayParam(newFilters.projectIds)
    const serviceIds = serializeArrayParam(newFilters.serviceIds)
    const severities = serializeArrayParam(newFilters.severities)

    if (projectIds) params.projectIds = projectIds
    if (serviceIds) params.serviceIds = serviceIds
    if (severities) params.severities = severities

    setSearchParams(params, { replace: true })
  }, [setSearchParams])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  // Convert filters to API params format
  const apiParams = useMemo(() => {
    const params = {}
    if (filters.projectIds.length > 0) params.projectId = filters.projectIds
    if (filters.serviceIds.length > 0) params.serviceId = filters.serviceIds
    if (filters.severities.length > 0) params.severity = filters.severities[0]
    return params
  }, [filters])

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !isEmpty(filters.projectIds) ||
           !isEmpty(filters.serviceIds) ||
           !isEmpty(filters.severities)
  }, [filters])

  return {
    filters,
    setFilters,
    clearFilters,
    apiParams,
    hasActiveFilters,
  }
}

export default useLogFilters
